import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Plus, LogOut, Store, ExternalLink, Settings2, Trash2, Save, Users, CalendarDays, Mail,
  CreditCard, Layers, Check, RefreshCw, KeyRound, Copy, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const SUB_BADGE = {
  active: { label: "Abonné", cls: "border-[#5E8C5E]/50 text-[#8FBF8F]" },
  past_due: { label: "Retard", cls: "border-[#C5A059]/50 text-[#C5A059]" },
  expired: { label: "Expiré", cls: "border-[#8C3131]/50 text-[#E08D8D]" },
  none: { label: "Sans abo.", cls: "border-[#6B6760]/40 text-[#6B6760]" },
};
const fmtPrice = (a) => `${Number(a).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; } };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [salons, setSalons] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [manageId, setManageId] = useState(null);
  const [tab, setTab] = useState("salons");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/salons");
      setSalons(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <Toaster theme="dark" />
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0908]/85 border-b border-[#2C2A26]">
        <div className="max-w-6xl mx-auto px-6 h-24 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <div className="flex flex-col leading-none">
              <span className="font-display text-3xl tracking-[0.15em] text-[#F4F0EA]">BARBER</span>
              <span className="font-mono-caps text-[10px] tracking-[0.45em] text-[#C5A059] mt-1">ESPACE ADMIN</span>
            </div>
            <nav className="hidden md:flex items-center gap-8 text-xs uppercase tracking-[0.2em]">
              <button onClick={() => setTab("salons")} className={tab === "salons" ? "text-[#C5A059]" : "text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="admin-tab-salons">Salons</button>
              <button onClick={() => setTab("plans")} className={tab === "plans" ? "text-[#C5A059]" : "text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="admin-tab-plans">Formules</button>
              <button onClick={() => setTab("stripe")} className={tab === "stripe" ? "text-[#C5A059]" : "text-[#A39E93] hover:text-[#F4F0EA]"} data-testid="admin-tab-stripe">Paiement</button>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-[#A39E93]">{user?.email}</span>
            <button onClick={handleLogout} className="text-[#A39E93] hover:text-[#C5A059] transition flex items-center gap-2 text-xs uppercase tracking-[0.2em]" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" strokeWidth={1.5} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      {tab === "plans" ? (
        <PlansManager />
      ) : tab === "stripe" ? (
        <StripeSettings />
      ) : (
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Multi-salon</div>
            <h1 className="font-serif text-4xl sm:text-5xl font-light">Vos salons.</h1>
            <p className="text-[#A39E93] mt-3 text-sm">Créez et gérez chaque salon, son propriétaire, son abonnement et sa configuration email.</p>
          </div>
          <button onClick={() => setOpenCreate(true)} className="btn-primary" data-testid="admin-new-salon-btn">
            <Plus className="w-4 h-4" strokeWidth={2} /> Nouveau salon
          </button>
        </div>

        {salons === null ? (
          <div className="text-[#A39E93] text-sm uppercase tracking-[0.3em]">Chargement…</div>
        ) : salons.length === 0 ? (
          <div className="border border-dashed border-[#2C2A26] p-10 text-center text-[#6B6760]">Aucun salon. Créez le premier.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="admin-salons-grid">
            {salons.map((s) => {
              const sb = SUB_BADGE[s.subscription_status] || SUB_BADGE.none;
              return (
              <div key={s.id} className="bg-[#141311] border border-[#2C2A26] p-6 hover:border-[#C5A059]/50 transition-colors" data-testid={`admin-salon-${s.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-full border border-[#C5A059]/40 flex items-center justify-center text-[#C5A059] shrink-0">
                      <Store className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-2xl font-light truncate">{s.name}</div>
                      <div className="font-mono-caps text-[10px] tracking-[0.25em] text-[#A39E93] truncate">/{s.slug}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 border ${s.active ? "border-[#C5A059]/40 text-[#C5A059]" : "border-[#8C3131]/40 text-[#8C3131]"}`}>
                      {s.active ? "Actif" : "Inactif"}
                    </span>
                    <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-1 border ${sb.cls}`} data-testid={`admin-salon-sub-${s.id}`}>{sb.label}</span>
                  </div>
                </div>

                <div className="mt-5 flex items-center gap-5 text-xs text-[#A39E93]">
                  <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {s.staff} coiffeur(s)</span>
                  <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {s.appointments} RDV</span>
                  <span className={`inline-flex items-center gap-1.5 ${s.emails_enabled ? "text-[#C5A059]" : ""}`}><Mail className="w-3.5 h-3.5" /> {s.emails_enabled ? "Emails ON" : "Emails OFF"}</span>
                </div>
                <div className="mt-2 text-xs text-[#6B6760] truncate">Propriétaire : {s.owner_email || "—"}</div>
                <div className="mt-1 text-xs text-[#6B6760] truncate inline-flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> {s.plan_name || "Aucune formule"}{s.current_period_end ? ` · échéance ${fmtDate(s.current_period_end)}` : ""}
                </div>

                <div className="mt-5 pt-4 border-t border-[#2C2A26] flex items-center gap-4">
                  <button onClick={() => setManageId(s.id)} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#D9B873] inline-flex items-center gap-2" data-testid={`admin-manage-${s.id}`}>
                    <Settings2 className="w-4 h-4" strokeWidth={1.5} /> Gérer
                  </button>
                  <Link to={`/salon/${s.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.2em] text-[#A39E93] hover:text-[#F4F0EA] inline-flex items-center gap-2" data-testid={`admin-view-${s.id}`}>
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Voir le site
                  </Link>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
      )}

      <CreateSalonDialog open={openCreate} setOpen={setOpenCreate} onCreated={load} />
      <ManageSalonDialog salonId={manageId} onClose={() => setManageId(null)} onChanged={load} />
    </div>
  );
}

function StripeSettings() {
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("test");
  const [keys, setKeys] = useState({ test_secret_key: "", test_webhook_secret: "", live_secret_key: "", live_webhook_secret: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/stripe-settings"); setCfg(data); setMode(data.mode); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { mode };
      Object.entries(keys).forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
      const { data } = await api.patch("/admin/stripe-settings", payload);
      setCfg(data);
      setKeys({ test_secret_key: "", test_webhook_secret: "", live_secret_key: "", live_webhook_secret: "" });
      toast.success("Configuration Stripe enregistrée");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const copyWebhook = () => { navigator.clipboard?.writeText(cfg.webhook_url); toast.success("URL du webhook copiée"); };

  if (!cfg) return <div className="max-w-3xl mx-auto px-6 py-12 text-[#A39E93] text-sm uppercase tracking-[0.3em]">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 space-y-10">
      <div>
        <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Paiement</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light">Configuration Stripe.</h1>
        <p className="text-[#A39E93] mt-3 text-sm">Saisissez vos identifiants Stripe. Tant qu&apos;aucune clé n&apos;est renseignée pour le mode actif, le paiement est désactivé.</p>
      </div>

      {/* Status */}
      <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] px-3 py-1.5 border ${cfg.active ? "border-[#5E8C5E]/50 text-[#8FBF8F]" : "border-[#8C3131]/50 text-[#E08D8D]"}`} data-testid="stripe-status">
        {cfg.active ? <ShieldCheck className="w-4 h-4" strokeWidth={1.5} /> : <ShieldAlert className="w-4 h-4" strokeWidth={1.5} />}
        {cfg.active ? `Paiement actif (mode ${cfg.mode === "live" ? "production" : "test"})` : "Paiement non configuré"}
      </div>

      {/* Mode selector */}
      <section className="space-y-3">
        <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Mode</Label>
        <div className="flex gap-3" data-testid="stripe-mode-selector">
          {[["test", "Test"], ["live", "Production"]].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)} className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${mode === v ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/10" : "border-[#2C2A26] text-[#A39E93] hover:text-[#F4F0EA]"}`} data-testid={`stripe-mode-${v}`}>{l}</button>
          ))}
        </div>
        <p className="text-xs text-[#6B6760]">Le mode « Production » utilise vos clés <span className="font-mono">sk_live_…</span> pour encaisser réellement. Le mode « Test » utilise vos clés <span className="font-mono">sk_test_…</span>.</p>
      </section>

      {/* Test keys */}
      <section className="bg-[#141311] border border-[#2C2A26] p-6 space-y-4" data-testid="stripe-test-section">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] inline-flex items-center gap-2"><KeyRound className="w-4 h-4" /> Clés Test {cfg.test_secret_set && <span className="text-[#8FBF8F]">· configurées</span>}</div>
        <KeyField label="Clé secrète Test (sk_test_…)" value={keys.test_secret_key} onChange={(v) => setKeys({ ...keys, test_secret_key: v })} set={cfg.test_secret_set} testId="stripe-test-secret" />
        <KeyField label="Secret du webhook Test (whsec_…)" value={keys.test_webhook_secret} onChange={(v) => setKeys({ ...keys, test_webhook_secret: v })} set={cfg.test_webhook_set} testId="stripe-test-webhook" />
      </section>

      {/* Live keys */}
      <section className="bg-[#141311] border border-[#2C2A26] p-6 space-y-4" data-testid="stripe-live-section">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] inline-flex items-center gap-2"><KeyRound className="w-4 h-4" /> Clés Production {cfg.live_secret_set && <span className="text-[#8FBF8F]">· configurées</span>}</div>
        <KeyField label="Clé secrète Production (sk_live_…)" value={keys.live_secret_key} onChange={(v) => setKeys({ ...keys, live_secret_key: v })} set={cfg.live_secret_set} testId="stripe-live-secret" />
        <KeyField label="Secret du webhook Production (whsec_…)" value={keys.live_webhook_secret} onChange={(v) => setKeys({ ...keys, live_webhook_secret: v })} set={cfg.live_webhook_set} testId="stripe-live-webhook" />
      </section>

      <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50" data-testid="stripe-save"><Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Enregistrement…" : "Enregistrer la configuration"}</button>

      {/* Webhook URL */}
      <section className="bg-[#0A0908] border border-[#2C2A26] p-6 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">URL du webhook</div>
        <p className="text-xs text-[#6B6760]">Dans votre tableau de bord Stripe → Développeurs → Webhooks, créez un endpoint avec cette URL (elle s&apos;adapte automatiquement à votre domaine), puis copiez le secret <span className="font-mono">whsec_…</span> ci-dessus.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input value={cfg.webhook_url} readOnly className="bg-[#141311] border-[#2C2A26] font-mono text-xs" data-testid="stripe-webhook-url" />
          <button onClick={copyWebhook} className="btn-secondary whitespace-nowrap" data-testid="stripe-copy-webhook"><Copy className="w-4 h-4" /> Copier</button>
        </div>
      </section>
    </div>
  );
}

function KeyField({ label, value, onChange, set, testId }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</Label>
      <Input type="password" value={value} onChange={(e) => onChange(e.target.value)} placeholder={set ? "•••••••••••• (enregistrée — laisser vide pour conserver)" : "Coller la clé"} className="mt-2 bg-[#0A0908] border-[#2C2A26] font-mono" data-testid={testId} />
    </div>
  );
}

function PlansManager() {
  const [plans, setPlans] = useState(null);
  const empty = { name: "", description: "", amount: "", interval: "month", features: "", active: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/plans"); setPlans(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description,
        amount: parseFloat(form.amount), interval: form.interval, active: form.active,
        features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
      };
      await api.post("/admin/plans", payload);
      toast.success("Formule créée");
      setForm(empty);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const update = async (id, patch) => {
    try { await api.patch(`/admin/plans/${id}`, patch); toast.success("Formule mise à jour"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette formule ? Les salons déjà abonnés conservent leur période en cours.")) return;
    try { await api.delete(`/admin/plans/${id}`); toast.success("Formule supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-2">Abonnements</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light">Vos formules.</h1>
        <p className="text-[#A39E93] mt-3 text-sm">Définissez librement vos formules et tarifs (en euros). Elles seront proposées aux propriétaires dans leur espace « Abonnement ».</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Create form */}
        <form onSubmit={create} className="bg-[#141311] border border-[#2C2A26] p-6 space-y-4 self-start" data-testid="plan-create-form">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Nouvelle formule</div>
          <AField label="Nom *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="plan-name" required />
          <AField label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="plan-description" />
          <div className="grid grid-cols-2 gap-4">
            <AField label="Prix (€) *" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="plan-amount" required />
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Facturation</Label>
              <select value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid="plan-interval">
                <option value="month">Par mois</option>
                <option value="year">Par an</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Avantages (un par ligne)</Label>
            <textarea value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={4} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" placeholder={"Réservation illimitée\nAgenda multi-coiffeurs"} data-testid="plan-features" />
          </div>
          <button type="submit" disabled={saving} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="plan-create-submit">
            {saving ? "Création…" : "Créer la formule"}
          </button>
        </form>

        {/* List */}
        <div className="lg:col-span-2 space-y-4">
          {plans === null ? (
            <div className="text-[#A39E93] text-sm uppercase tracking-[0.3em]">Chargement…</div>
          ) : plans.length === 0 ? (
            <div className="border border-dashed border-[#2C2A26] p-10 text-center text-[#6B6760]">Aucune formule. Créez la première.</div>
          ) : plans.map((p) => <PlanRow key={p.id} plan={p} onUpdate={update} onRemove={remove} />)}
        </div>
      </div>
    </div>
  );
}

function PlanRow({ plan, onUpdate, onRemove }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ name: plan.name, description: plan.description || "", amount: plan.amount, interval: plan.interval, features: (plan.features || []).join("\n") });
  const save = () => {
    onUpdate(plan.id, {
      name: f.name, description: f.description, amount: parseFloat(f.amount), interval: f.interval,
      features: f.features.split("\n").map((x) => x.trim()).filter(Boolean),
    });
    setEdit(false);
  };
  return (
    <div className="bg-[#141311] border border-[#2C2A26] p-5" data-testid={`plan-row-${plan.id}`}>
      {edit ? (
        <div className="space-y-3">
          <AField label="Nom" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId={`plan-edit-name-${plan.id}`} />
          <AField label="Description" value={f.description} onChange={(v) => setF({ ...f, description: v })} testId={`plan-edit-desc-${plan.id}`} />
          <div className="grid grid-cols-2 gap-3">
            <AField label="Prix (€)" type="number" value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testId={`plan-edit-amount-${plan.id}`} />
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Facturation</Label>
              <select value={f.interval} onChange={(e) => setF({ ...f, interval: e.target.value })} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]">
                <option value="month">Par mois</option><option value="year">Par an</option>
              </select>
            </div>
          </div>
          <textarea value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} rows={3} className="w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" />
          <div className="flex gap-3">
            <button onClick={save} className="btn-primary text-xs" data-testid={`plan-edit-save-${plan.id}`}><Save className="w-4 h-4" /> Enregistrer</button>
            <button onClick={() => setEdit(false)} className="btn-secondary text-xs">Annuler</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-2xl font-light">{plan.name}</span>
              <span className="font-display text-xl text-[#C5A059]">{fmtPrice(plan.amount)}<span className="text-xs text-[#6B6760]"> / {plan.interval === "year" ? "an" : "mois"}</span></span>
              {!plan.active && <span className="text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border border-[#6B6760]/40 text-[#6B6760]">Inactive</span>}
            </div>
            {plan.description && <p className="text-sm text-[#A39E93] mt-1">{plan.description}</p>}
            {plan.features?.length > 0 && (
              <ul className="mt-3 space-y-1">
                {plan.features.map((x, i) => <li key={i} className="flex items-center gap-2 text-xs text-[#A39E93]"><Check className="w-3.5 h-3.5 text-[#C5A059]" /> {x}</li>)}
              </ul>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <Switch checked={plan.active} onCheckedChange={(v) => onUpdate(plan.id, { active: v })} data-testid={`plan-active-${plan.id}`} />
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#A39E93]">{plan.active ? "Active" : "Inactive"}</span>
            </div>
            <button onClick={() => setEdit(true)} className="text-xs uppercase tracking-[0.2em] text-[#C5A059] hover:text-[#D9B873] inline-flex items-center gap-1.5" data-testid={`plan-edit-${plan.id}`}><Settings2 className="w-4 h-4" /> Modifier</button>
            <button onClick={() => onRemove(plan.id)} className="text-xs uppercase tracking-[0.2em] text-[#8C3131] hover:text-[#E08D8D] inline-flex items-center gap-1.5" data-testid={`plan-delete-${plan.id}`}><Trash2 className="w-4 h-4" /> Supprimer</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateSalonDialog({ open, setOpen, onCreated }) {
  const empty = { name: "", slug: "", owner_email: "", owner_password: "", owner_name: "", phone: "", address: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.slug) delete payload.slug;
      const { data } = await api.post("/admin/salons", payload);
      toast.success(`Salon « ${data.salon.name} » créé`);
      setForm(empty);
      setOpen(false);
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#141311] border-[#2C2A26] text-[#F4F0EA] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light">Nouveau salon</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="create-salon-form">
          <AField label="Nom du salon *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="create-salon-name" required />
          <AField label="Slug (URL) — optionnel" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="auto depuis le nom" testId="create-salon-slug" />
          <div className="grid grid-cols-2 gap-4">
            <AField label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="create-salon-phone" />
            <AField label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="create-salon-address" />
          </div>
          <div className="pt-2 border-t border-[#2C2A26]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-4 mt-4">Compte propriétaire</div>
            <AField label="Nom du propriétaire" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testId="create-owner-name" />
            <div className="mt-4"><AField label="Email de connexion *" type="email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} testId="create-owner-email" required /></div>
            <div className="mt-4"><AField label="Mot de passe * (min. 8 caractères)" type="password" value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} testId="create-owner-password" required /></div>
          </div>
          <button type="submit" disabled={saving} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="create-salon-submit">
            {saving ? "Création…" : "Créer le salon"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ManageSalonDialog({ salonId, onClose, onChanged }) {
  const [salon, setSalon] = useState(null);
  const [form, setForm] = useState(null);
  const [owner, setOwner] = useState({ email: "", password: "", name: "" });
  const [pwdSet, setPwdSet] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [plans, setPlans] = useState([]);

  const reload = async () => {
    const { data } = await api.get(`/admin/salons/${salonId}`);
    setSalon(data);
    return data;
  };

  useEffect(() => {
    if (!salonId) { setSalon(null); setForm(null); return; }
    (async () => {
      try {
        const [{ data }, pl] = await Promise.all([api.get(`/admin/salons/${salonId}`), api.get("/admin/plans")]);
        setSalon(data);
        setPlans(pl.data);
        setForm({
          name: data.name || "", slug: data.slug || "", active: data.active !== false,
          phone: data.phone || "", address: data.address || "", email: data.email || "",
          plan_id: data.plan_id || "",
          emails_enabled: data.emails_enabled || false,
          email_from_name: data.email_from_name || data.name || "",
          email_from_address: data.email_from_address || "",
          site_url: data.site_url || "",
          smtp_host: data.smtp_host || "", smtp_port: data.smtp_port || 587,
          smtp_security: data.smtp_security || "tls", smtp_user: data.smtp_user || "",
          smtp_password: "", owner_notify_email: data.owner_notify_email || "",
        });
        setOwner({ email: data.owner?.email || "", password: "", name: data.owner?.name || "" });
        setPwdSet(!!data.smtp_password_set);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [salonId]);

  const extendSub = async () => {
    try {
      await api.post(`/admin/salons/${salonId}/subscription/extend`);
      toast.success("Paiement enregistré, abonnement prolongé");
      await reload();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveSalon = async () => {
    try {
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      if (salon?.is_default) delete payload.slug;
      const { data } = await api.patch(`/admin/salons/${salonId}`, payload);
      setPwdSet(!!data.smtp_password_set);
      setForm((f) => ({ ...f, smtp_password: "" }));
      toast.success("Salon enregistré");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveOwner = async () => {
    try {
      const payload = {};
      if (owner.email) payload.email = owner.email;
      if (owner.name !== undefined) payload.name = owner.name;
      if (owner.password) payload.password = owner.password;
      await api.patch(`/admin/salons/${salonId}/owner`, payload);
      setOwner((o) => ({ ...o, password: "" }));
      toast.success("Propriétaire mis à jour");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async () => {
    const to = testTo || form?.owner_notify_email;
    if (!to) { toast.error("Renseignez une adresse de test"); return; }
    try {
      await api.post(`/admin/salons/${salonId}/email/test`, { to });
      toast.success(`Email de test envoyé à ${to}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSalon = async () => {
    if (!window.confirm("Supprimer définitivement ce salon et TOUTES ses données (RDV, clients, coiffeurs) ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/admin/salons/${salonId}`);
      toast.success("Salon supprimé");
      onClose();
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!salonId) return null;

  return (
    <Dialog open={!!salonId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#141311] border-[#2C2A26] text-[#F4F0EA] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light">{salon?.name || "Gérer le salon"}</DialogTitle>
        </DialogHeader>
        {!form ? (
          <div className="text-[#A39E93] text-sm py-8">Chargement…</div>
        ) : (
          <div className="space-y-8" data-testid="manage-salon-body">
            {/* Infos */}
            <section className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Informations</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AField label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="manage-name" />
                <AField label="Slug (URL)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} testId="manage-slug" disabled={salon?.is_default} />
                <AField label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="manage-phone" />
                <AField label="Email public" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="manage-email" />
                <AField label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="manage-address" className="md:col-span-2" />
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#0A0908] border border-[#2C2A26]">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="manage-active" />
                <span className="text-sm">{form.active ? "Salon actif (visible publiquement)" : "Salon désactivé (masqué)"}</span>
              </div>
            </section>

            {/* Abonnement */}
            <section className="space-y-4" data-testid="manage-subscription">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Abonnement</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Formule assignée</Label>
                  <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid="manage-plan-select">
                    <option value="">— Aucune —</option>
                    {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · {fmtPrice(p.amount)}/{p.interval === "year" ? "an" : "mois"}</option>)}
                  </select>
                </div>
                <div className="text-sm">
                  {(() => {
                    const sb = SUB_BADGE[salon?.subscription?.status] || SUB_BADGE.none;
                    return <span className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 border ${sb.cls}`} data-testid="manage-sub-status">{sb.label}</span>;
                  })()}
                  {salon?.subscription?.current_period_end && (
                    <div className="text-xs text-[#6B6760] mt-2">Échéance : {fmtDate(salon.subscription.current_period_end)} ({salon.subscription.days_remaining} j)</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-[#6B6760]">Assignez une formule puis « Enregistrer le salon ». Utilisez « Enregistrer un paiement » pour prolonger d&apos;une période (ex : virement reçu).</p>
              <button onClick={extendSub} disabled={!form.plan_id} className="btn-secondary text-xs disabled:opacity-40" data-testid="manage-extend-sub">
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Enregistrer un paiement (prolonger)
              </button>
            </section>

            {/* SMTP */}
            <section className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Emails & SMTP</div>
              <div className="flex items-center gap-3 p-4 bg-[#0A0908] border border-[#2C2A26]">
                <Switch checked={form.emails_enabled} onCheckedChange={(v) => setForm({ ...form, emails_enabled: v })} data-testid="manage-emails-enabled" />
                <span className="text-sm">{form.emails_enabled ? "Emails activés" : "Emails désactivés"}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AField label="Nom d'expéditeur" value={form.email_from_name} onChange={(v) => setForm({ ...form, email_from_name: v })} testId="manage-from-name" />
                <AField label="Email d'expéditeur" value={form.email_from_address} onChange={(v) => setForm({ ...form, email_from_address: v })} testId="manage-from-address" />
                <AField label="URL du site (liens emails)" value={form.site_url} onChange={(v) => setForm({ ...form, site_url: v })} testId="manage-site-url" className="md:col-span-2" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <AField label="Serveur SMTP" value={form.smtp_host} onChange={(v) => setForm({ ...form, smtp_host: v })} placeholder="smtp.gmail.com" testId="manage-smtp-host" className="md:col-span-2" />
                <AField label="Port" type="number" value={form.smtp_port} onChange={(v) => setForm({ ...form, smtp_port: parseInt(v) || 587 })} testId="manage-smtp-port" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">Sécurité</Label>
                  <select value={form.smtp_security} onChange={(e) => setForm({ ...form, smtp_security: e.target.value })} className="mt-2 w-full bg-[#0A0908] border border-[#2C2A26] p-2.5 text-sm text-[#F4F0EA]" data-testid="manage-smtp-security">
                    <option value="tls">STARTTLS (587)</option>
                    <option value="ssl">SSL/TLS (465)</option>
                    <option value="none">Aucune</option>
                  </select>
                </div>
                <AField label="Identifiant" value={form.smtp_user} onChange={(v) => setForm({ ...form, smtp_user: v })} testId="manage-smtp-user" />
                <AField label="Mot de passe" type="password" value={form.smtp_password} onChange={(v) => setForm({ ...form, smtp_password: v })} placeholder={pwdSet ? "•••••• (enregistré)" : "Mot de passe"} testId="manage-smtp-password" />
              </div>
              <AField label="Email de notification du propriétaire" value={form.owner_notify_email} onChange={(v) => setForm({ ...form, owner_notify_email: v })} testId="manage-notify-email" />
            </section>

            <button onClick={saveSalon} className="btn-primary w-full justify-center" data-testid="manage-save-btn"><Save className="w-4 h-4" strokeWidth={1.5} /> Enregistrer le salon</button>

            <div className="p-4 bg-[#0A0908] border border-[#2C2A26]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-3">Tester l'envoi</div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="adresse@email.fr" className="bg-[#141311] border-[#2C2A26]" data-testid="manage-test-to" />
                <button onClick={sendTest} className="btn-secondary whitespace-nowrap" data-testid="manage-test-send">Envoyer un test</button>
              </div>
            </div>

            {/* Owner */}
            <section className="space-y-4 pt-2 border-t border-[#2C2A26]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mt-4">Compte propriétaire</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AField label="Nom" value={owner.name} onChange={(v) => setOwner({ ...owner, name: v })} testId="manage-owner-name" />
                <AField label="Email de connexion" type="email" value={owner.email} onChange={(v) => setOwner({ ...owner, email: v })} testId="manage-owner-email" />
                <AField label="Nouveau mot de passe (laisser vide pour ne pas changer)" type="password" value={owner.password} onChange={(v) => setOwner({ ...owner, password: v })} testId="manage-owner-password" className="md:col-span-2" />
              </div>
              <button onClick={saveOwner} className="btn-secondary" data-testid="manage-owner-save"><Save className="w-4 h-4" strokeWidth={1.5} /> Mettre à jour le propriétaire</button>
            </section>

            {/* Danger */}
            {!salon?.is_default && (
              <section className="pt-2 border-t border-[#8C3131]/30">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#8C3131] mt-4 mb-3">Zone dangereuse</div>
                <button onClick={deleteSalon} className="text-[#8C3131] hover:text-[#E08D8D] inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#8C3131]/40 px-4 py-2.5" data-testid="manage-delete-btn">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Supprimer ce salon
                </button>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AField({ label, value, onChange, testId, type = "text", placeholder = "", required = false, disabled = false, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-[0.2em] text-[#A39E93]">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="mt-2 bg-[#0A0908] border-[#2C2A26] disabled:opacity-50"
        data-testid={testId}
      />
    </div>
  );
}
